/**
 * 🐻 SLEEPY BEAR TEMPERAMENT
 * Deep hibernation slowly wakes to HANGRY RAGE!
 * Don't wake a hungry bear...
 *
 * Created by Tom & Azura - January 14, 2026
 */

import type { Temperament } from '../types';

export const SleepyBear: Temperament = {
  id: 'sleepy-bear',
  name: 'Sleepy Bear',
  emoji: '🐻',
  description: 'Deep hibernation builds hunger until HANGRY RAGE! Feed me or else!',

  // ============================================================
  // MOODS - Building toward HANGRY
  // ============================================================

  moods: ['hibernating', 'stirring', 'groggy', 'hungry', 'hangry', 'stuffed'],
  initialMood: 'hibernating',

  // ============================================================
  // MOOD TRANSITIONS
  // ============================================================

  moodMatrix: {
    hibernating: [
      { mood: 'hibernating', weight: 0.6 },
      { mood: 'stirring', weight: 0.4 },
    ],
    stirring: [
      { mood: 'hibernating', weight: 0.2 },
      { mood: 'stirring', weight: 0.3 },
      { mood: 'groggy', weight: 0.5 },
    ],
    groggy: [
      { mood: 'stirring', weight: 0.15 },
      { mood: 'groggy', weight: 0.35 },
      { mood: 'hungry', weight: 0.5 },
    ],
    hungry: [
      { mood: 'groggy', weight: 0.1 },
      { mood: 'hungry', weight: 0.4 },
      { mood: 'hangry', weight: 0.5 },
    ],
    hangry: [
      { mood: 'hangry', weight: 0.55 },
      { mood: 'stuffed', weight: 0.45 },
    ],
    stuffed: [
      { mood: 'stuffed', weight: 0.25 },
      { mood: 'hibernating', weight: 0.7 },
      { mood: 'stirring', weight: 0.05 },
    ],
  },

  // ============================================================
  // MOOD DURATIONS
  // ============================================================

  moodDurations: {
    hibernating: { base: 3500, variance: 4000 },
    stirring:    { base: 600, variance: 800 },
    groggy:      { base: 500, variance: 700 },
    hungry:      { base: 400, variance: 500 },
    hangry:      { base: 250, variance: 350 },
    stuffed:     { base: 1500, variance: 2000 },
  },

  moodConstraints: {
    hangry: {
      minDuration: 2500,
      maxDuration: 6000,
    },
  },

  // ============================================================
  // ENERGY SYSTEM - Builds hunger!
  // ============================================================

  energy: {
    max: 100,
    initial: 0,
    regenRate: 5,

    moodDrain: {
      hibernating: -2,   // Slowly getting hungry in sleep
      stirring:    -3,   // Waking = more hungry
      groggy:      -2,   // Stomach growling...
      hungry:      -1,   // Almost hangry...
      hangry:      20,   // Burns energy ROARING
      stuffed:     -6,   // Food coma recovery
    },

    thresholds: {
      exhausted: 8,
      low: 25,
      high: 55,
      explosive: 75,
    },

    restingMoods: ['hibernating', 'stuffed', 'stirring', 'groggy'],
  },

  // ============================================================
  // EXPRESSIONS
  // ============================================================

  expressions: {
    hibernating: {
      rotation: { x: -20, y: -25, z: 2 },
      translation: { x: 0, y: 2, z: 0 },
      scale: 1,
      transitionMs: 600,
      jitter: 0.3,
      glowIntensity: 0.1,
      glowColor: 'rgba(139, 90, 43, 0.3)',
    },
    stirring: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 1, z: 0 },
      scale: 1,
      transitionMs: 300,
      jitter: 2,
      glowIntensity: 0.2,
      glowColor: 'rgba(139, 90, 43, 0.4)',
    },
    groggy: {
      rotation: { x: -18, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 200,
      jitter: 3,
      glowIntensity: 0.3,
      glowColor: 'rgba(200, 150, 50, 0.5)',
    },
    hungry: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -1, z: 0 },
      scale: 1.01,
      transitionMs: 120,
      jitter: 5,
      glowIntensity: 0.5,
      glowColor: 'rgba(255, 150, 0, 0.6)',
    },
    hangry: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -2, z: 0 },
      scale: 1.04,
      transitionMs: 60,
      jitter: 7,
      glowIntensity: 1,
      glowColor: 'rgba(255, 80, 80, 0.9)',
      cssClass: 'hangry',
    },
    stuffed: {
      rotation: { x: -22, y: -25, z: 3 },
      translation: { x: 0, y: 3, z: 0 },
      scale: 1.02,
      transitionMs: 500,
      jitter: 0.5,
      glowIntensity: 0.2,
      glowColor: 'rgba(100, 200, 100, 0.4)',
    },
  },

  // ============================================================
  // CORNER BOUNCING (for hangry mode - heavy!)
  // ============================================================

  corners: {
    center: {
      transform: { x: 0, y: 0, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      bulge: { x: 50, y: 50 },
    },
    topLeft: {
      transform: { x: -2, y: 2, z: -1 },
      translation: { x: -4, y: -3, z: 0 },
      bulge: { x: 25, y: 25 },
    },
    topRight: {
      transform: { x: -2, y: -2, z: 1 },
      translation: { x: 4, y: -3, z: 0 },
      bulge: { x: 75, y: 25 },
    },
    bottomLeft: {
      transform: { x: 2, y: 2, z: 1 },
      translation: { x: -4, y: 3, z: 0 },
      bulge: { x: 25, y: 75 },
    },
    bottomRight: {
      transform: { x: 2, y: -2, z: -1 },
      translation: { x: 4, y: 3, z: 0 },
      bulge: { x: 75, y: 75 },
    },
  },

  bouncingMoods: ['hangry', 'hungry'],

  bounceIntervals: {
    hangry: 200,
    hungry: 400,
  },

  // ============================================================
  // STIMULI HANDLERS
  // ============================================================

  stimulusHandlers: {
    time: (_stimulus, currentMood, energy) => {
      if (currentMood === 'hibernating' && energy < 15) {
        return { energyDelta: 4 };
      }
      if (currentMood === 'hibernating' && energy >= 15) {
        return { transitionTo: 'stirring', energyDelta: 3 };
      }
      if (currentMood === 'stirring') {
        return { transitionTo: 'groggy', energyDelta: 2 };
      }
      return null;
    },

    poke: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Poking wakes the bear - and makes it HUNGRY!
      if (currentMood === 'hibernating') {
        return { transitionTo: 'stirring', energyDelta: 18 * intensity };
      }
      if (currentMood === 'stirring') {
        return { transitionTo: 'groggy', energyDelta: 18 * intensity };
      }
      if (currentMood === 'groggy') {
        return { transitionTo: 'hungry', energyDelta: 22 * intensity };
      }
      if (currentMood === 'hungry') {
        return { transitionTo: 'hangry', energyDelta: 25 * intensity, forceDuration: 2500 };
      }
      if (currentMood === 'stuffed') {
        return { transitionTo: 'groggy', energyDelta: 12 * intensity };
      }
      return { energyDelta: 12 * intensity };
    },

    shake: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Shaking a bear is a BAD IDEA!
      if (currentMood === 'hibernating' || currentMood === 'stirring') {
        return { transitionTo: 'hungry', energyDelta: 35 * intensity };
      }
      if (currentMood === 'groggy' || currentMood === 'hungry' || energy > 40) {
        return { transitionTo: 'hangry', energyDelta: 40 * intensity, forceDuration: 3500 };
      }
      return { transitionTo: 'hungry', energyDelta: 30 * intensity };
    },
  },

  // ============================================================
  // MOOD INDICATORS
  // ============================================================

  moodIndicators: {
    hibernating: {
      emoji: '😴',
      text: 'ZZZ',
      label: '*deep hibernation*',
    },
    stirring: {
      emoji: '🥱',
      text: 'zzz',
      label: '*yaaaawn*',
    },
    groggy: {
      emoji: '😪',
      text: '...',
      label: '*rubbing eyes*',
    },
    hungry: {
      emoji: '🤤',
      text: '?!',
      label: '*stomach growling*',
    },
    hangry: {
      emoji: '🐻',
      text: '!!!',
      label: '🍖 FEED ME NOW! 🍖',
    },
    stuffed: {
      emoji: '😋',
      text: '~~~',
      label: '*food coma*',
    },
  },
};
